import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { carePlanService } from '../services/carePlanService'
import { prescriptionService } from '../services/prescriptionService'
import { medicationService } from '../services/medicationService'
import {
  CarePlan,
  CarePlanFormData,
  CarePlanGoal,
  CarePlanIntervention,
  CarePlanOutcome,
  CarePlanStatus,
} from '../types/carePlan'
import { Prescription } from '../types/prescription'
import { PatientMedication } from '../types/medication'
import { formatDate } from '../utils/locale'
import './CarePlanForm.css'

// Helper function to generate UUIDs
function randomUUID(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

interface CarePlanFormProps {
  patientId?: string // Optional for templates
  carePlan?: CarePlan // If provided, edit mode
  onSuccess?: () => void
  onCancel?: () => void
}

export function CarePlanForm({ patientId, carePlan, onSuccess, onCancel }: CarePlanFormProps) {
  const queryClient = useQueryClient()
  const isEditing = !!carePlan

  // Basic form data
  const [title, setTitle] = useState(carePlan?.title || '')
  const [description, setDescription] = useState(carePlan?.description || '')
  const [status, setStatus] = useState<CarePlanStatus>(carePlan?.status || 'draft')
  const [startDate, setStartDate] = useState(
    carePlan?.startDate ? carePlan.startDate.split('T')[0] : ''
  )
  const [endDate, setEndDate] = useState(carePlan?.endDate ? carePlan.endDate.split('T')[0] : '')
  const [reviewDate, setReviewDate] = useState(
    carePlan?.reviewDate ? carePlan.reviewDate.split('T')[0] : ''
  )
  const [notes, setNotes] = useState(carePlan?.notes || '')

  // Goals, Interventions, Outcomes state
  const [goals, setGoals] = useState<CarePlanGoal[]>(carePlan?.goals || [])
  const [interventions, setInterventions] = useState<CarePlanIntervention[]>(
    carePlan?.interventions || []
  )
  const [outcomes, setOutcomes] = useState<CarePlanOutcome[]>(carePlan?.outcomes || [])

  // Fetch prescriptions and medications for Rx linking (only if patientId provided)
  const { data: prescriptions } = useQuery<Prescription[]>({
    queryKey: ['prescriptions', patientId],
    queryFn: async () => {
      if (!patientId) return []
      const response = await prescriptionService.listPatientPrescriptions(patientId)
      return response.items || []
    },
    enabled: !!patientId,
  })

  const { data: medications } = useQuery<PatientMedication[]>({
    queryKey: ['patient-medications', patientId],
    queryFn: async () => {
      if (!patientId) return []
      const response = await medicationService.getPatientMedications(patientId)
      return response.items || []
    },
    enabled: !!patientId,
  })

  const createMutation = useMutation({
    mutationFn: (data: CarePlanFormData) => {
      if (!patientId) {
        throw new Error('patientId is required for creating care plans')
      }
      return carePlanService.create({ ...data, patientId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['care-plans'] })
      if (patientId) {
        queryClient.invalidateQueries({ queryKey: ['care-plans', patientId] })
      }
      onSuccess?.()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CarePlanFormData>) => {
      if (!carePlan) throw new Error('Care plan is required for update')
      return carePlanService.update(carePlan.id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['care-plans'] })
      queryClient.invalidateQueries({ queryKey: ['care-plan', carePlan?.id] })
      if (patientId) {
        queryClient.invalidateQueries({ queryKey: ['care-plans', patientId] })
      }
      onSuccess?.()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) {
      alert('Title is required')
      return
    }

    const formData: CarePlanFormData = {
      patientId,
      title: title.trim(),
      description: description.trim() || undefined,
      status,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      reviewDate: reviewDate || undefined,
      notes: notes.trim() || undefined,
      goals: goals.length > 0 ? goals : undefined,
      interventions: interventions.length > 0 ? interventions : undefined,
      outcomes: outcomes.length > 0 ? outcomes : undefined,
      // Preserve template-related fields if editing
      isTemplate: carePlan?.isTemplate,
      templateCategory: carePlan?.templateCategory,
      templateId: carePlan?.templateId,
      conditionId: carePlan?.conditionId,
      assessmentId: carePlan?.assessmentId,
      consultationId: carePlan?.consultationId,
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(formData)
      } else {
        await createMutation.mutateAsync(formData)
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to save care plan')
    }
  }

  // Goal management
  const addGoal = () => {
    const newGoal: CarePlanGoal = {
      id: randomUUID(),
      description: '',
      status: 'not-started',
    }
    setGoals([...goals, newGoal])
  }

  const updateGoal = (id: string, updates: Partial<CarePlanGoal>) => {
    setGoals(goals.map((g) => (g.id === id ? { ...g, ...updates } : g)))
  }

  const removeGoal = (id: string) => {
    setGoals(goals.filter((g) => g.id !== id))
  }

  // Intervention management
  const addIntervention = () => {
    const newIntervention: CarePlanIntervention = {
      id: randomUUID(),
      type: 'other',
      description: '',
      status: 'not-started',
    }
    setInterventions([...interventions, newIntervention])
  }

  const updateIntervention = (id: string, updates: Partial<CarePlanIntervention>) => {
    setInterventions(interventions.map((i) => (i.id === id ? { ...i, ...updates } : i)))
  }

  const removeIntervention = (id: string) => {
    setInterventions(interventions.filter((i) => i.id !== id))
  }

  // Outcome management
  const addOutcome = () => {
    const newOutcome: CarePlanOutcome = {
      id: randomUUID(),
      description: '',
      status: 'pending',
    }
    setOutcomes([...outcomes, newOutcome])
  }

  const updateOutcome = (id: string, updates: Partial<CarePlanOutcome>) => {
    setOutcomes(outcomes.map((o) => (o.id === id ? { ...o, ...updates } : o)))
  }

  const removeOutcome = (id: string) => {
    setOutcomes(outcomes.filter((o) => o.id !== id))
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <form onSubmit={handleSubmit} className="care-plan-form">
      <div className="form-section">
        <h2>{isEditing ? 'Edit Care Plan' : 'Create Care Plan'}</h2>

        <div className="form-group">
          <label htmlFor="title">
            Title <span className="required">*</span>
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            disabled={isLoading}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value as CarePlanStatus)}
              disabled={isLoading}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="startDate">Start Date</label>
            <input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="endDate">End Date</label>
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="reviewDate">Review Date</label>
            <input
              id="reviewDate"
              type="date"
              value={reviewDate}
              onChange={(e) => setReviewDate(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Goals Section */}
      <div className="form-section">
        <div className="section-header">
          <h3>Goals ({goals.length})</h3>
          <button type="button" onClick={addGoal} disabled={isLoading} className="btn-add">
            + Add Goal
          </button>
        </div>
        {goals.length === 0 ? (
          <p className="empty-state">No goals defined. Click "Add Goal" to add one.</p>
        ) : (
          <div className="items-list">
            {goals.map((goal) => (
              <div key={goal.id} className="item-card">
                <div className="item-header">
                  <span className="item-number">Goal {goals.indexOf(goal) + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeGoal(goal.id)}
                    disabled={isLoading}
                    className="btn-remove"
                  >
                    Remove
                  </button>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={goal.description}
                    onChange={(e) => updateGoal(goal.id, { description: e.target.value })}
                    rows={2}
                    disabled={isLoading}
                    placeholder="Goal description"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={goal.status}
                      onChange={(e) =>
                        updateGoal(goal.id, {
                          status: e.target.value as CarePlanGoal['status'],
                        })
                      }
                      disabled={isLoading}
                    >
                      <option value="not-started">Not Started</option>
                      <option value="in-progress">In Progress</option>
                      <option value="achieved">Achieved</option>
                      <option value="not-achieved">Not Achieved</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Target Date</label>
                    <input
                      type="date"
                      value={goal.targetDate ? goal.targetDate.split('T')[0] : ''}
                      onChange={(e) =>
                        updateGoal(goal.id, { targetDate: e.target.value || undefined })
                      }
                      disabled={isLoading}
                    />
                  </div>
                </div>
                {goal.status === 'achieved' && (
                  <div className="form-group">
                    <label>Achieved Date</label>
                    <input
                      type="date"
                      value={goal.achievedDate ? goal.achievedDate.split('T')[0] : ''}
                      onChange={(e) =>
                        updateGoal(goal.id, { achievedDate: e.target.value || undefined })
                      }
                      disabled={isLoading}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={goal.notes || ''}
                    onChange={(e) => updateGoal(goal.id, { notes: e.target.value || undefined })}
                    rows={2}
                    disabled={isLoading}
                    placeholder="Additional notes"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Interventions Section */}
      <div className="form-section">
        <div className="section-header">
          <h3>Interventions ({interventions.length})</h3>
          <button type="button" onClick={addIntervention} disabled={isLoading} className="btn-add">
            + Add Intervention
          </button>
        </div>
        {interventions.length === 0 ? (
          <p className="empty-state">No interventions defined. Click "Add Intervention" to add one.</p>
        ) : (
          <div className="items-list">
            {interventions.map((intervention) => (
              <div key={intervention.id} className="item-card">
                <div className="item-header">
                  <span className="item-number">Intervention {interventions.indexOf(intervention) + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeIntervention(intervention.id)}
                    disabled={isLoading}
                    className="btn-remove"
                  >
                    Remove
                  </button>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={intervention.type}
                      onChange={(e) =>
                        updateIntervention(intervention.id, {
                          type: e.target.value as CarePlanIntervention['type'],
                          // Clear Rx links if type changes from medication
                          ...(intervention.type === 'medication' && e.target.value !== 'medication'
                            ? { prescriptionId: undefined, medicationId: undefined }
                            : {}),
                        })
                      }
                      disabled={isLoading}
                    >
                      <option value="medication">Medication</option>
                      <option value="therapy">Therapy</option>
                      <option value="lifestyle">Lifestyle</option>
                      <option value="monitoring">Monitoring</option>
                      <option value="referral">Referral</option>
                      <option value="education">Education</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={intervention.status}
                      onChange={(e) =>
                        updateIntervention(intervention.id, {
                          status: e.target.value as CarePlanIntervention['status'],
                        })
                      }
                      disabled={isLoading}
                    >
                      <option value="not-started">Not Started</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="discontinued">Discontinued</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={intervention.description}
                    onChange={(e) => updateIntervention(intervention.id, { description: e.target.value })}
                    rows={2}
                    disabled={isLoading}
                    placeholder="Intervention description"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Frequency</label>
                    <input
                      type="text"
                      value={intervention.frequency || ''}
                      onChange={(e) =>
                        updateIntervention(intervention.id, { frequency: e.target.value || undefined })
                      }
                      disabled={isLoading}
                      placeholder="e.g., Daily, Twice daily, Weekly"
                    />
                  </div>
                  <div className="form-group">
                    <label>Duration</label>
                    <input
                      type="text"
                      value={intervention.duration || ''}
                      onChange={(e) =>
                        updateIntervention(intervention.id, { duration: e.target.value || undefined })
                      }
                      disabled={isLoading}
                      placeholder="e.g., 2 weeks, 30 days"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={intervention.startDate ? intervention.startDate.split('T')[0] : ''}
                      onChange={(e) =>
                        updateIntervention(intervention.id, { startDate: e.target.value || undefined })
                      }
                      disabled={isLoading}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={intervention.endDate ? intervention.endDate.split('T')[0] : ''}
                      onChange={(e) =>
                        updateIntervention(intervention.id, { endDate: e.target.value || undefined })
                      }
                      disabled={isLoading}
                    />
                  </div>
                </div>
                {/* Rx Linking for Medication Type */}
                {intervention.type === 'medication' && patientId && (
                  <div className="form-group">
                    <label>Link to Prescription or Medication</label>
                    <div className="rx-linking">
                      <div className="form-group">
                        <label htmlFor={`prescription-${intervention.id}`}>Prescription</label>
                        <select
                          id={`prescription-${intervention.id}`}
                          value={intervention.prescriptionId || ''}
                          onChange={(e) =>
                            updateIntervention(intervention.id, {
                              prescriptionId: e.target.value || undefined,
                              medicationId: e.target.value ? undefined : intervention.medicationId, // Clear medication if prescription selected
                            })
                          }
                          disabled={isLoading}
                        >
                          <option value="">None</option>
                          {prescriptions?.map((prescription) => (
                            <option key={prescription.prescriptionId} value={prescription.prescriptionId}>
                              {formatDate(prescription.prescriptionDate)} -{' '}
                              {prescription.medications.map((m) => m.name).join(', ')} (
                              {prescription.status})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor={`medication-${intervention.id}`}>Patient Medication</label>
                        <select
                          id={`medication-${intervention.id}`}
                          value={intervention.medicationId || ''}
                          onChange={(e) =>
                            updateIntervention(intervention.id, {
                              medicationId: e.target.value || undefined,
                              prescriptionId: e.target.value ? undefined : intervention.prescriptionId, // Clear prescription if medication selected
                            })
                          }
                          disabled={isLoading}
                        >
                          <option value="">None</option>
                          {medications?.map((medication) => (
                            <option key={medication.medicationId} value={medication.medicationId}>
                              {medication.name} - {medication.status}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={intervention.notes || ''}
                    onChange={(e) =>
                      updateIntervention(intervention.id, { notes: e.target.value || undefined })
                    }
                    rows={2}
                    disabled={isLoading}
                    placeholder="Additional notes"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Outcomes Section */}
      <div className="form-section">
        <div className="section-header">
          <h3>Outcomes ({outcomes.length})</h3>
          <button type="button" onClick={addOutcome} disabled={isLoading} className="btn-add">
            + Add Outcome
          </button>
        </div>
        {outcomes.length === 0 ? (
          <p className="empty-state">No outcomes defined. Click "Add Outcome" to add one.</p>
        ) : (
          <div className="items-list">
            {outcomes.map((outcome) => (
              <div key={outcome.id} className="item-card">
                <div className="item-header">
                  <span className="item-number">Outcome {outcomes.indexOf(outcome) + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeOutcome(outcome.id)}
                    disabled={isLoading}
                    className="btn-remove"
                  >
                    Remove
                  </button>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={outcome.description}
                    onChange={(e) => updateOutcome(outcome.id, { description: e.target.value })}
                    rows={2}
                    disabled={isLoading}
                    placeholder="Outcome description"
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={outcome.status}
                    onChange={(e) =>
                      updateOutcome(outcome.id, {
                        status: e.target.value as CarePlanOutcome['status'],
                      })
                    }
                    disabled={isLoading}
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="achieved">Achieved</option>
                    <option value="not-achieved">Not Achieved</option>
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Measurement Method</label>
                    <input
                      type="text"
                      value={outcome.measurement || ''}
                      onChange={(e) =>
                        updateOutcome(outcome.id, { measurement: e.target.value || undefined })
                      }
                      disabled={isLoading}
                      placeholder="How to measure this outcome"
                    />
                  </div>
                  <div className="form-group">
                    <label>Target Value</label>
                    <input
                      type="text"
                      value={outcome.targetValue || ''}
                      onChange={(e) =>
                        updateOutcome(outcome.id, { targetValue: e.target.value || undefined })
                      }
                      disabled={isLoading}
                      placeholder="Target value"
                    />
                  </div>
                  <div className="form-group">
                    <label>Current Value</label>
                    <input
                      type="text"
                      value={outcome.currentValue || ''}
                      onChange={(e) =>
                        updateOutcome(outcome.id, { currentValue: e.target.value || undefined })
                      }
                      disabled={isLoading}
                      placeholder="Current value"
                    />
                  </div>
                </div>
                {outcome.currentValue && (
                  <div className="form-group">
                    <label>Measured Date</label>
                    <input
                      type="date"
                      value={outcome.measuredDate ? outcome.measuredDate.split('T')[0] : ''}
                      onChange={(e) =>
                        updateOutcome(outcome.id, { measuredDate: e.target.value || undefined })
                      }
                      disabled={isLoading}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={outcome.notes || ''}
                    onChange={(e) => updateOutcome(outcome.id, { notes: e.target.value || undefined })}
                    rows={2}
                    disabled={isLoading}
                    placeholder="Additional notes"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} disabled={isLoading} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" disabled={isLoading || !title.trim()} className="btn-primary">
          {isLoading ? 'Saving...' : isEditing ? 'Update Care Plan' : 'Create Care Plan'}
        </button>
      </div>
    </form>
  )
}
